export type CountryRatesDraft = {
  NGN: { buy: string; sell: string };
  GHS: { buy: string; sell: string };
  KES: { buy: string; sell: string };
  offset: string;
};
export type FormFields = Array<{
  key: string;
  label: string;
  unit?: string;
  min?: number;
  max?: number;
  positive?: boolean;
}>;

export function countryRatesFromConfig(config: any): CountryRatesDraft {
  const read = (side: string, code: string) => {
    const value = config?.[side];
    // Keep legacy scalar USD fields NGN-only. Never use them as GHS/KES FX.
    const rate =
      code === "NGN" && typeof value?.usd !== "object" && value?.usd != null
        ? value.usd
        : (value?.usdRates?.[code] ?? value?.usd?.[code]);
    return String(rate ?? (code === "NGN" ? "" : 0));
  };
  return {
    NGN: { buy: read("buy", "NGN"), sell: read("sell", "NGN") },
    GHS: { buy: read("buy", "GHS"), sell: read("sell", "GHS") },
    KES: { buy: read("buy", "KES"), sell: read("sell", "KES") },
    offset: String(config?.altcoinSellOffsetPercent ?? config?.btcPercent ?? 0),
  };
}

export function validateCountryRates(draft: CountryRatesDraft): string {
  for (const code of ["NGN", "GHS", "KES"] as const) {
    for (const side of ["buy", "sell"] as const) {
      const value = draft[code][side];
      if (
        value.trim() === "" ||
        !Number.isFinite(Number(value)) ||
        Number(value) < 0 ||
        (code === "NGN" && Number(value) === 0)
      )
        return `${code} ${side} rate must be ${code === "NGN" ? "above zero" : "zero or above"} and finite.`;
    }
  }
  if (
    draft.offset.trim() === "" ||
    !Number.isFinite(Number(draft.offset)) ||
    Number(draft.offset) < 0 ||
    Number(draft.offset) >= 100
  )
    return "Altcoin sell offset must be at least 0% and less than 100%.";
  return "";
}

export const btcFields: FormFields = [
  {
    key: "maxFeePerByte",
    label: "Maximum fee per byte",
    unit: "sat/byte",
    min: 1,
  },
  { key: "minInputAmount", label: "Minimum input amount", unit: "BTC", min: 0 },
  { key: "minTotalSweep", label: "Minimum sweep amount", unit: "USD", min: 0 },
  {
    key: "minDefaultAmount",
    label: "Minimum default input",
    unit: "BTC",
    min: 0,
    positive: true,
  },
  {
    key: "maxDefaultAmount",
    label: "Maximum input amount",
    unit: "BTC",
    min: 0,
    positive: true,
  },
];
export const feeFields: FormFields = [
  { key: "virtualCardFundingFee", label: "Virtual card funding" },
  { key: "billPaymentCrypto", label: "Bill payment · crypto wallet" },
  { key: "p2pFiatSendFee", label: "P2P fiat send" },
  { key: "billPaymentFiat.NG", label: "Nigeria · fiat bill payment" },
  { key: "billPaymentFiat.GH", label: "Ghana · fiat bill payment" },
  { key: "billPaymentFiat.KE", label: "Kenya · fiat bill payment" },
  { key: "mobileMoneyDeposit.GH.mtnMomo", label: "Ghana · MTN MoMo deposit" },
  {
    key: "mobileMoneyDeposit.GH.airtelTigo",
    label: "Ghana · AirtelTigo deposit",
  },
  {
    key: "mobileMoneyDeposit.GH.vodafoneCash",
    label: "Ghana · Vodafone Cash deposit",
  },
  { key: "mobileMoneyDeposit.KE.mpesa", label: "Kenya · M-Pesa deposit" },
  {
    key: "mobileMoneyDeposit.KE.airtelMoney",
    label: "Kenya · Airtel Money deposit",
  },
].map((field) => ({ ...field, unit: "%", min: 0, max: 100 }));

export const fieldsFromConfig = (
  config: any,
  fields: FormFields,
): Record<string, string> =>
  Object.fromEntries(
    fields.map(({ key }) => [
      key,
      String(
        key.split(".").reduce((value, part) => value?.[part], config) ?? "",
      ),
    ]),
  );

export function validateFields(
  draft: Record<string, string>,
  fields: FormFields,
): string {
  for (const field of fields) {
    const raw = draft[field.key] ?? "";
    const number = Number(raw);
    if (
      raw.trim() === "" ||
      !Number.isFinite(number) ||
      number < (field.min ?? 0) ||
      (field.positive && number === 0) ||
      (field.max !== undefined && number > field.max)
    )
      return `${field.label} must be a finite number ${field.positive ? "above zero" : `at least ${field.min ?? 0}`}${field.max !== undefined ? ` and no more than ${field.max}` : ""}.`;
  }
  if (
    draft.minDefaultAmount !== undefined &&
    Number(draft.minDefaultAmount) > Number(draft.maxDefaultAmount)
  )
    return "Minimum default input cannot exceed maximum input amount.";
  return "";
}
